import React, { useState, useEffect } from 'react';

const NoteModal = ({ isOpen, onClose, book, chapter, verseNumber, note, onSave, onDelete }) => {
  const [noteContent, setNoteContent] = useState('');
  const [noteTitle, setNoteTitle] = useState('');
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false); // State for close confirmation
  const [isDirty, setIsDirty] = useState(false); // Track if note has been modified

  const verse = verseNumber % 1000;

  useEffect(() => {
    if (note) {
      setNoteContent(note.text);
      setNoteTitle(note.title || '');
    } else {
      setNoteContent('');
      setNoteTitle('');
    }
    setIsDirty(false); // Reset dirty state when modal is opened
  }, [note]);

  const handleSave = () => {
    if (!noteContent.trim()) {
      alert('Note content cannot be empty.');
      return;
    }
    onSave(noteContent, noteTitle);
    setShowConfirmSave(true);
    setIsDirty(false); // Reset dirty state after saving
  };

  const handleDelete = () => {
    setShowConfirmDelete(true);
  };

  const confirmDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setShowConfirmDelete(false);
    onClose();
  };

  const confirmSaveOk = () => {
    setShowConfirmSave(false);
    onClose();
  };

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  const cancelClose = () => {
    setShowConfirmClose(false);
  };

  return isOpen ? (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div
        className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-lg"
        style={{ animation: 'fadeIn 0.3s ease-in-out' }}
      >
        <h2 className="text-2xl font-bold mb-4">
          {note ? 'Edit Note' : 'New Note'} {book} {chapter}:{verse}
        </h2>
        <input
          type="text"
          placeholder="Note title (optional)"
          value={noteTitle}
          onChange={(e) => {
            setNoteTitle(e.target.value);
            setIsDirty(true); // Mark as dirty when content changes
          }}
          className="w-full p-2 mb-4 border border-gray-300 rounded-md"
        />
        <textarea
          value={noteContent}
          onChange={(e) => {
            setNoteContent(e.target.value);
            setIsDirty(true); // Mark as dirty when content changes
          }}
          placeholder="Write your note here..."
          className="w-full p-2 border border-gray-300 rounded-md"
          style={{ minHeight: '150px' }}
        />
        <div className="flex justify-end space-x-2 mt-4">
          <button
            onClick={handleSave}
            className="bg-[#04657a] text-white px-4 py-2 rounded-md hover:bg-[#034857]"
          >
            Save
          </button>
          {note && (
            <button
              onClick={handleDelete}
              className="bg-[#a83820] text-white px-4 py-2 rounded-md hover:bg-[#732311]"
            >
              Delete
            </button>
          )}
          <button
            onClick={handleClose}
            className="bg-[#b87a25] text-white px-4 py-2 rounded-md hover:bg-[#915d13]"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showConfirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-sm">
            <p className="text-lg font-semibold">Are you sure you want to delete this note?</p>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={confirmDelete}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
              >
                Yes, Delete
              </button>
              <button
                onClick={() => setShowConfirmDelete(false)}
                className="bg-gray-300 text-black px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Confirmation Dialog */}
      {showConfirmSave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-sm">
            <p className="text-lg font-semibold">The note has been successfully saved.</p>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={confirmSaveOk}
                className="bg-[#983b10] text-white px-4 py-2 rounded-md hover:bg-[#6e2604]"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Confirmation Dialog */}
      {showConfirmClose && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-sm">
            <p className="text-lg font-semibold">You have unsaved changes. Are you sure you want to close without saving?</p>
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={confirmClose}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600"
              >
                Yes, Close
              </button>
              <button
                onClick={cancelClose}
                className="bg-gray-300 text-black px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  ) : null;
};

export default NoteModal;
